import {
  type APIGuildScheduledEvent,
  type APIUser,
  canInGuild,
  PermissionFlags,
} from '@alternativamais/chat-core';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { create } from 'zustand';

import { avisar, avisarErro } from '../../../componentes/Avisos';
import { useFecharFora } from '../../../componentes/Flutuante';
import { Icone, type NomeIcone } from '../../../componentes/Icone';
import { useCliente, useDerivado, useEstado } from '../../../chat/contexto';

/** Status do Discord: 1 agendado, 2 acontecendo, 3 terminou, 4 cancelado. */
export const STATUS_EVENTO = {
  agendado: 1,
  acontecendo: 2,
  terminou: 3,
  cancelado: 4,
};

/** Quando a lista de eventos de cada servidor foi buscada. */
const buscadoEm: Record<string, number> = {};

/** Interesses da pessoa (o botão "Interessado"), por evento. */
const useInteresses = create<{
  meus: Record<string, boolean>;
  carregados: Record<string, boolean>;
}>(() => ({ meus: {}, carregados: {} }));

/**
 * Eventos do servidor (próximos e acontecendo) e os interesses da pessoa:
 * carrega ao abrir e acompanha o gateway.
 */
export function useEventos(guildId: string) {
  const cliente = useCliente();
  const euId = useEstado((estado) => estado.me?.id);

  useEffect(() => {
    // A lateral, a lista e o detalhe usam isto juntos: uma busca por vez
    // (depois o gateway mantém em dia; o READY zera e busca de novo).
    const agora = Date.now();
    const temNaLoja = !!cliente.store.getState().scheduledEvents[guildId];
    const ultima = buscadoEm[guildId] ?? 0;
    if (agora - ultima > (temNaLoja ? 5 * 60_000 : 30_000)) {
      buscadoEm[guildId] = agora;
      cliente.api
        .scheduledEvents(guildId, true)
        .then((lista) => {
          const loja = cliente.store.getState();
          for (const evento of lista)
            loja.dispatch('GUILD_SCHEDULED_EVENT_CREATE', evento);
        })
        .catch((erro) => {
          delete buscadoEm[guildId];
          avisarErro(erro);
        });
    }
    if (!useInteresses.getState().carregados[guildId]) {
      useInteresses.setState((atual) => ({
        ...atual,
        carregados: { ...atual.carregados, [guildId]: true },
      }));
      cliente.api
        .myScheduledEvents([guildId])
        .then((lista) =>
          useInteresses.setState((atual) => ({
            ...atual,
            meus: {
              ...atual.meus,
              ...Object.fromEntries(
                lista.map((item) => [item.guild_scheduled_event_id, true]),
              ),
            },
          })),
        )
        .catch(() =>
          useInteresses.setState((atual) => ({
            ...atual,
            carregados: { ...atual.carregados, [guildId]: false },
          })),
        );
    }
    const parar = cliente.subscribe((tipo, dados) => {
      if (
        tipo !== 'GUILD_SCHEDULED_EVENT_USER_ADD' &&
        tipo !== 'GUILD_SCHEDULED_EVENT_USER_REMOVE'
      )
        return;
      const mudanca = dados as {
        guild_scheduled_event_id: string;
        user_id: string;
      };
      if (mudanca.user_id !== euId) return;
      useInteresses.setState((atual) => ({
        ...atual,
        meus: {
          ...atual.meus,
          [mudanca.guild_scheduled_event_id]:
            tipo === 'GUILD_SCHEDULED_EVENT_USER_ADD',
        },
      }));
    });
    return parar;
  }, [cliente, guildId, euId]);

  const eventos = useDerivado(
    (estado) =>
      Object.values(estado.scheduledEvents[guildId] ?? {})
        .filter(
          (evento) =>
            evento.status === STATUS_EVENTO.agendado ||
            evento.status === STATUS_EVENTO.acontecendo,
        )
        .sort(
          (a, b) =>
            new Date(a.scheduled_start_time).getTime() -
            new Date(b.scheduled_start_time).getTime(),
        ),
    (estado) => [estado.scheduledEvents[guildId]],
  );
  return eventos;
}

export function useInteressado(eventoId: string): boolean {
  return useInteresses((estado) => !!estado.meus[eventoId]);
}

/** Marcar ou tirar o interesse (otimista; o gateway confirma a contagem). */
export function useAlternarInteresse() {
  const cliente = useCliente();
  return (evento: APIGuildScheduledEvent, interessado: boolean) => {
    useInteresses.setState((atual) => ({
      ...atual,
      meus: { ...atual.meus, [evento.id]: interessado },
    }));
    cliente.api
      .setEventInterest(evento.guild_id, evento.id, interessado)
      .catch((erro) => {
        useInteresses.setState((atual) => ({
          ...atual,
          meus: { ...atual.meus, [evento.id]: !interessado },
        }));
        avisarErro(erro);
      });
  };
}

/** Quem pode criar evento; quem pode editar este (quem criou ou gerencia). */
export function usePodeCriarEvento(guildId: string) {
  return useEstado(
    (estado) =>
      canInGuild(estado, guildId, PermissionFlags.CREATE_EVENTS) ||
      canInGuild(estado, guildId, PermissionFlags.MANAGE_EVENTS),
  );
}

export function usePodeEditarEvento(
  evento: APIGuildScheduledEvent | undefined,
) {
  return useEstado(
    (estado) =>
      !!evento &&
      (canInGuild(estado, evento.guild_id, PermissionFlags.MANAGE_EVENTS) ||
        (evento.creator_id === estado.me?.id &&
          canInGuild(estado, evento.guild_id, PermissionFlags.CREATE_EVENTS))),
  );
}

const DIA = new Intl.DateTimeFormat('pt-BR', {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const HORA = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit',
  minute: '2-digit',
});

/** "SEX, 2 DE OUT · 14:00–16:00" (como o cartão do design). */
export function quandoDoEvento(
  evento: Pick<
    APIGuildScheduledEvent,
    'scheduled_start_time' | 'scheduled_end_time'
  >,
): string {
  const inicio = new Date(evento.scheduled_start_time);
  // "sáb., 3 de out." → "sáb, 3 de out"
  const dia = DIA.format(inicio).replace(/\./g, '');
  const fim = evento.scheduled_end_time
    ? new Date(evento.scheduled_end_time)
    : null;
  const mesmoDia = fim && fim.toDateString() === inicio.toDateString();
  const horas = fim
    ? mesmoDia
      ? `${HORA.format(inicio)}–${HORA.format(fim)}`
      : `${HORA.format(inicio)} até ${DIA.format(fim).replace(/\./g, '')} ${HORA.format(fim)}`
    : HORA.format(inicio);
  return `${dia} · ${horas}`.toUpperCase();
}

export function linkDoEvento(evento: APIGuildScheduledEvent): string {
  return `${window.location.origin}/canais/${evento.guild_id}?evento=${evento.id}`;
}

export function compartilhar(evento: APIGuildScheduledEvent) {
  void navigator.clipboard
    ?.writeText(linkDoEvento(evento))
    .then(() => avisar('Link do evento copiado.', 'info'))
    .catch(() => avisar('Não deu para copiar.'));
}

/** Local escrito parece link? (abre em nova aba). */
export function ehLink(local: string): boolean {
  return /^https?:\/\/\S+$/i.test(local.trim());
}

/** Interessados (os primeiros, para os avatares; a lista completa no detalhe). */
export function useInteressados(
  evento: APIGuildScheduledEvent,
  limite: number,
) {
  const cliente = useCliente();
  const contagem = evento.user_count;
  const [pessoas, setPessoas] = useState<APIUser[] | null>(null);
  useEffect(() => {
    let vivo = true;
    cliente.api
      .scheduledEventUsers(evento.guild_id, evento.id, { limit: limite })
      .then((lista) => {
        if (!vivo) return;
        cliente.store.getState().upsertUsers(lista.map((item) => item.user));
        setPessoas(lista.map((item) => item.user));
      })
      .catch(() => vivo && setPessoas([]));
    return () => {
      vivo = false;
    };
    // A contagem mudou: alguém marcou ou tirou; busca de novo.
  }, [cliente, evento.guild_id, evento.id, limite, contagem]);
  return pessoas;
}

/** Janela com cabeçalho (título, extras, fechar) e corpo rolável. */
export function Janela({
  titulo,
  icone,
  extras,
  largura = 640,
  aoFechar,
  children,
}: {
  titulo: string;
  icone?: NomeIcone;
  extras?: ReactNode;
  largura?: number;
  aoFechar: () => void;
  children: ReactNode;
}) {
  const id = useId();
  const cartao = useRef<HTMLDivElement>(null);
  useFecharFora(true, aoFechar, cartao);
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(5,6,8,.72)] p-4">
      <div
        ref={cartao}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titulo`}
        className="flex max-h-full flex-col overflow-hidden rounded-xl bg-elevado shadow-[0_12px_32px_rgba(0,0,0,.45)]"
        style={{ width: largura }}
      >
        <header className="flex shrink-0 items-center gap-2.5 border-b border-borda py-4 pr-4 pl-5">
          {icone && (
            <Icone nome={icone} tamanho={22} className="text-texto-2" />
          )}
          <h2
            id={`${id}-titulo`}
            className="m-0 font-titulo text-[22px] leading-7 font-bold"
          >
            {titulo}
          </h2>
          {extras}
          <span className="grow" />
          <button
            type="button"
            aria-label="Fechar"
            onClick={aoFechar}
            className="flex size-9 shrink-0 items-center justify-center rounded-lg text-texto-2 hover:text-texto"
          >
            <Icone nome="x" tamanho={20} />
          </button>
        </header>
        <div className="rolagem flex min-h-0 flex-col gap-3 overflow-y-auto px-5 pt-4 pb-5">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
